// The app's icon glyphs. They live in two worlds: Leaflet divIcons take raw
// HTML strings, React renders the legend and the tow banner — so each glyph
// ships from one shared definition, rendered the same way in both, so the two
// can never drift.

type Glyph = {
  viewBox: string;
  // Attributes on the <svg> itself. Stroke-drawn glyphs carry their own set;
  // filled ones just take currentColor, so the pin's class picks the ink —
  // dark on the white "you" pin, white on a colored one.
  attrs: string;
  // Inner markup, verbatim from the source icon.
  body: string;
};

const GLYPHS = {
  // ant-design:car-filled — a repair shop. A plain car reads as "where the car
  // goes" without the toolbox connotation of a wrench, which people also read
  // as "settings".
  car: {
    viewBox: "0 0 1024 1024",
    attrs: 'fill="currentColor"',
    body: '<path d="M959 413.4L935.3 372a8 8 0 0 0-10.9-2.9l-50.7 29.6l-78.3-216.2a63.9 63.9 0 0 0-60.9-44.4H301.2c-34.7 0-65.5 22.4-76.2 55.5l-74.6 205.2l-50.8-29.6a8 8 0 0 0-10.9 2.9L65 413.4c-2.2 3.8-.9 8.6 2.9 10.8l60.4 35.2l-14.5 40c-1.2 3.2-1.8 6.6-1.8 10v348.2c0 15.7 11.8 28.4 26.3 28.4h67.6c12.3 0 23-9.3 25.6-22.3l7.7-37.7h545.6l7.7 37.7c2.7 13 13.3 22.3 25.6 22.3h67.6c14.5 0 26.3-12.7 26.3-28.4V509.4c0-3.4-.6-6.8-1.8-10l-14.5-40l60.3-35.2a8 8 0 0 0 3-10.8M264 621c-22.1 0-40-17.9-40-40s17.9-40 40-40s40 17.9 40 40s-17.9 40-40 40m388 75c0 4.4-3.6 8-8 8H380c-4.4 0-8-3.6-8-8v-84c0-4.4 3.6-8 8-8h40c4.4 0 8 3.6 8 8v36h168v-36c0-4.4 3.6-8 8-8h40c4.4 0 8 3.6 8 8zm108-75c-22.1 0-40-17.9-40-40s17.9-40 40-40s40 17.9 40 40s-17.9 40-40 40M220 418l72.7-199.9l.5-1.3l.4-1.3c1.1-3.3 4.1-5.5 7.6-5.5h427.6l75.4 208z"/>',
  },
  // game-icons:tow-truck — a tow company. The boom is the whole point: it is
  // what separates this pin from the shop car at a glance.
  tow: {
    viewBox: "0 0 512 512",
    attrs: 'fill="currentColor"',
    body: '<path d="M80.08 82.66L60.24 102.5L192.9 242.7v58.6h78v-50.9zm-25.4 40.14v80.3l4.01 2.7c10.56 7 14.74 14.1 15.93 19.8c1.18 5.8-.43 10.8-3.85 14.9c-6.86 8.3-19.91 12.3-32.73-.6l-12.72 12.8c19.18 19.1 46.13 15.1 59.27-.6c6.58-7.9 9.97-18.9 7.65-30.1c-2.05-10-8.72-19.7-19.56-28v-52.2zm258.02 52.5v144h-185c22.6 5.8 40.6 23.5 46.7 46H337c7.5-27.6 32.8-48 62.7-48s55.2 20.4 62.7 48h24.3v-84.6l-60.2-105.4zm36 14h62.1l54.7 92H348.7v-83zm-321.49 130l11.5 46h10.25c6.12-22.5 24.09-40.2 46.74-46zm84.49 16c-26.08 0-47.02 20.9-47.02 47s20.94 47 47.02 47c26.1 0 47-20.9 47-47s-20.9-47-47-47m288 0c-26.1 0-47 20.9-47 47s20.9 47 47 47s47-20.9 47-47s-20.9-47-47-47"/>',
  },
  // temaki:junk-car — the junk yard. A wrecked car reads instantly as
  // "salvage" in a way a generic parts glyph does not, and it stays legible at
  // pin size where finer line art would mush together.
  junkCar: {
    viewBox: "0 0 15 15",
    attrs: 'fill="currentColor"',
    body: '<path d="m10.57 4.38l-3.48.87a.38.38 0 0 1-.46-.27c-.05-.2.08-.41.28-.46l1.56-.39l-3.4-1.1L3.76 5.4l3.74.89h4.71l-.53-2.06zm3.42 3.64l-.35 3.48l-.98-.1c-.05.55-.53.94-1.07.89a.99.99 0 0 1-.87-1.1l-1.79-1.7l-5.01.97l-.11.99c-.05.55-.53.95-1.07.89a.99.99 0 0 1-.87-1.1l.1-.99l-.97-.11l.36-3.48c.07-.69.59-1.24 1.26-1.33l1.71-3.1c.1-.15.27-.24.45-.23l4.76 1.52c.2.02 2.41-.62 2.46-.43l1.02 3.37c.64.23 1.04.87.97 1.56M3.3 9.28c.44.04.88-.31 1.32-1.07l-1.07-.13V6.89c-.83.27-1.27.63-1.32 1.07c-.06.66.41 1.25 1.07 1.32m8.55.26c.55.06.93-.12 1.15-.54q-.225-.375-1.05-.45c-.83-.09-1.52.06-1.55.34c-.03.27.62.56 1.45.65M6.6 12.01c-.15-.18-.33-.36-.6-.51c-1-.55 1.5-.5 2-.5s2-.05 2 .5S8.99 13 7.88 13c-.1 0-.19-.01-.27-.02c-.19.01-.4.02-.61.02c-1.1 0-2-.22-2-.5c0-.24.68-.44 1.6-.49"/>',
  },
  // akar-icons:person — you. A person, not a car: the pin marks where the
  // USER is standing, which is not always where their car ended up.
  person: {
    viewBox: "0 0 24 24",
    attrs: 'fill="none" stroke="currentColor" stroke-width="2"',
    body: '<circle cx="12" cy="7" r="5"/><path stroke-linecap="round" stroke-linejoin="round" d="M17 14h.352a3 3 0 0 1 2.976 2.628l.391 3.124A2 2 0 0 1 18.734 22H5.266a2 2 0 0 1-1.985-2.248l.39-3.124A3 3 0 0 1 6.649 14H7"/>',
  },
} satisfies Record<string, Glyph>;

export type GlyphName = keyof typeof GLYPHS;

// Markup string for Leaflet divIcon HTML.
export const glyphMarkup = (name: GlyphName, size: number) => {
  const g = GLYPHS[name];
  return `<svg viewBox="${g.viewBox}" width="${size}" height="${size}" ${g.attrs} aria-hidden="true">${g.body}</svg>`;
};

// React form for anywhere a glyph sits inline with text. It reuses the same
// markup the pins get — the inner HTML is a compile-time constant from the
// table above, never anything a user supplies.
export function Glyph({ name, size = 15 }: { name: GlyphName; size?: number }) {
  return (
    <span
      className="glyph"
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: glyphMarkup(name, size) }}
    />
  );
}
